import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { endpointAPI } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { toast } from 'react-hot-toast';

interface InstallCommands { linux: string; macos: string; windows: string; }

const EndpointAddPage: React.FC = () => {
  useDocumentTitle('Add Endpoint');
  const navigate = useNavigate();
  const { currentAsset } = useAuth();
  const [name, setName] = useState('');
  const [os, setOs] = useState('windows');
  const [submitting, setSubmitting] = useState(false);
  const [commands, setCommands] = useState<InstallCommands | null>(null);
  const [enrollCode, setEnrollCode] = useState<string>('');
  const [activeOs, setActiveOs] = useState<'windows' | 'macos' | 'linux'>('windows');

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentAsset) return;
    if (!name.trim()) { toast.error('Give the endpoint a name'); return; }
    setSubmitting(true);
    try {
      const res = await endpointAPI.create({ asset_id: currentAsset.id, name: name.trim(), os });
      if (res.data.status === 'success') {
        setCommands(res.data.data.install_commands);
        setEnrollCode(res.data.data.enrollment_code || '');
        setActiveOs(os as any);
        toast.success('Endpoint added — share the enrollment code or install command below');
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to add endpoint');
    } finally {
      setSubmitting(false);
    }
  };

  const copy = (text: string) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <DashboardLayout>
      <div className="container py-3" style={{ maxWidth: 820 }}>
        <button className="btn btn-link px-0 mb-2" onClick={() => navigate('/endpoints')}>
          <i className="bi bi-arrow-left me-1"></i>Back to Endpoints
        </button>
        <h3 className="mb-1"><i className="bi bi-plus-circle me-2"></i>Add Endpoint</h3>
        <p className="text-muted">Name the computer, then run the generated one-line installer on it. The agent enrolls itself and appears online here.</p>

        {!commands ? (
          <div className="card">
            <div className="card-body">
              <form onSubmit={submit}>
                <div className="mb-3">
                  <label className="form-label">Endpoint name</label>
                  <input className="form-control" value={name} onChange={e => setName(e.target.value)}
                    placeholder="e.g. Reception PC, Ahmed's MacBook, Warehouse-01" autoFocus />
                  <div className="form-text">A friendly name so you can recognize this machine in the dashboard.</div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Operating system</label>
                  <div className="btn-group d-flex" role="group">
                    {[
                      { v: 'windows', label: 'Windows', icon: 'bi-windows' },
                      { v: 'macos', label: 'macOS', icon: 'bi-apple' },
                      { v: 'linux', label: 'Linux', icon: 'bi-ubuntu' },
                    ].map(o => (
                      <button type="button" key={o.v}
                        className={`btn ${os === o.v ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setOs(o.v)}>
                        <i className={`bi ${o.icon} me-2`}></i>{o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button className="btn btn-primary" type="submit" disabled={submitting}>
                  {submitting ? <><span className="spinner-border spinner-border-sm me-2"></span>Adding…</> : 'Add Endpoint & Get Installer'}
                </button>
              </form>
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="card-body">
              <div className="alert alert-success d-flex align-items-center">
                <i className="bi bi-check-circle-fill me-2"></i>
                <div>Endpoint <strong>{name}</strong> created. Enroll it using the code (desktop app) or the install command (server/CLI).</div>
              </div>

              {enrollCode && (
                <div className="mb-4">
                  <label className="form-label fw-semibold">
                    <i className="bi bi-key me-2"></i>Enrollment code — for the CloudAyn Protection desktop app
                  </label>
                  <div className="input-group">
                    <input className="form-control font-monospace" readOnly value={enrollCode}
                      style={{ fontSize: 13 }} onFocus={(e) => e.target.select()} />
                    <button className="btn btn-primary" onClick={() => copy(enrollCode)}>
                      <i className="bi bi-clipboard me-1"></i>Copy code
                    </button>
                  </div>
                  <div className="form-text">Paste this into the desktop app's Register screen to link this machine.</div>
                </div>
              )}

              <label className="form-label fw-semibold"><i className="bi bi-terminal me-2"></i>Or install via command line</label>
              <ul className="nav nav-tabs mb-3">
                {(['windows', 'macos', 'linux'] as const).map(o => (
                  <li className="nav-item" key={o}>
                    <button className={`nav-link ${activeOs === o ? 'active' : ''}`} onClick={() => setActiveOs(o)}>
                      <i className={`bi ${o === 'windows' ? 'bi-windows' : o === 'macos' ? 'bi-apple' : 'bi-ubuntu'} me-2`}></i>
                      {o === 'macos' ? 'macOS' : o.charAt(0).toUpperCase() + o.slice(1)}
                    </button>
                  </li>
                ))}
              </ul>

              <div className="position-relative">
                <pre className="bg-dark text-light p-3 rounded" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontSize: 13 }}>
                  {activeOs === 'windows' && commands.windows}
                  {activeOs === 'macos' && commands.macos}
                  {activeOs === 'linux' && commands.linux}
                </pre>
                <button className="btn btn-sm btn-light position-absolute top-0 end-0 m-2"
                  onClick={() => copy(activeOs === 'windows' ? commands.windows : activeOs === 'macos' ? commands.macos : commands.linux)}>
                  <i className="bi bi-clipboard me-1"></i>Copy
                </button>
              </div>

              <div className="alert alert-info small mt-3 mb-0">
                <i className="bi bi-info-circle me-2"></i>
                {activeOs === 'windows'
                  ? 'Run in an elevated PowerShell (Run as Administrator).'
                  : 'Run in Terminal with sudo. On macOS you may be prompted to grant permissions.'}
              </div>

              <div className="d-flex gap-2 mt-4">
                <button className="btn btn-primary" onClick={() => navigate('/endpoints')}>
                  <i className="bi bi-list me-2"></i>Go to Endpoints
                </button>
                <button className="btn btn-outline-secondary" onClick={() => { setCommands(null); setName(''); }}>
                  <i className="bi bi-plus-circle me-2"></i>Add Another
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default EndpointAddPage;
